import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Bookmark, Check, ChevronLeft, MessageSquareQuote, Play, Trophy } from 'lucide-react';
import { http } from '@/lib/api';

function buildSteps(lesson) {
  const steps = [];
  (lesson.sections || []).forEach((s, i) => steps.push({ key: `s${i}`, heading: s.heading, body: s.body, bullets: s.bullets }));
  if ((lesson.scripts || []).length) steps.push({ key: 'scripts', heading: 'What you might say', scripts: lesson.scripts });
  steps.push({ key: 'reflect', heading: 'Reflect & apply', prompts: lesson.reflection || [], actions: lesson.actions || [] });
  if (lesson.practice) steps.push({ key: 'practice', heading: 'Practice with your Coach', practice: lesson.practice });
  return steps;
}

const MODULE_NAMES = ['Foundations', 'Going Deeper', 'In Practice', 'Mastery', 'Beyond the Basics'];
const PER_MODULE = 5;

// Auto-group a flat lesson list into named chapters/modules. Short courses (≤6
// lessons) stay a single unnamed module so we don't over-chunk them.
function moduleize(lessons) {
  if (lessons.length <= 6) return [{ name: null, lessons, start: 0 }];
  const modules = [];
  for (let i = 0; i < lessons.length; i += PER_MODULE) {
    modules.push({
      name: MODULE_NAMES[modules.length] || `Module ${modules.length + 1}`,
      lessons: lessons.slice(i, i + PER_MODULE),
      start: i,
    });
  }
  return modules;
}

// Where to resume: first in-progress lesson (last_step > 0, not completed),
// else the first not-yet-completed lesson. null when everything is done.
function resumeTarget(lessons) {
  return lessons.find((l) => !l.completed && (l.last_step || 0) > 0)
    || lessons.find((l) => !l.completed)
    || null;
}

export default function SupportCourse() {
  const { courseId } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [step, setStep] = useState(0);
  const deepLinkDone = useRef(false);

  const load = useCallback(() => {
    http.get(`/support/university/courses/${courseId}`).then((r) => setCourse(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [courseId]);
  useEffect(() => { load(); }, [load]);

  const steps = useMemo(() => (active ? buildSteps(active) : []), [active]);

  const openLesson = (l) => { setActive(l); setStep(l.last_step || 0); window.scrollTo(0, 0); };
  const closeLesson = () => { setActive(null); setStep(0); load(); };

  // Deep link: ?lesson=<id> from the catalog search jumps straight into that lesson.
  useEffect(() => {
    if (deepLinkDone.current || !course) return;
    const lessonId = searchParams.get('lesson');
    if (!lessonId) return;
    const target = (course.lessons || []).find((l) => l.id === lessonId);
    deepLinkDone.current = true;
    if (target) openLesson(target);
    setSearchParams({}, { replace: true });
  }, [course, searchParams, setSearchParams]);

  const next = async () => {
    if (step < steps.length - 1) {
      const ns = step + 1;
      setStep(ns);
      http.post(`/support/university/courses/${courseId}/lessons/${active.id}/progress`, { last_step: ns }).catch(() => {});
    } else {
      await http.post(`/support/university/courses/${courseId}/lessons/${active.id}/complete`).catch(() => {});
      closeLesson();
    }
  };

  const toggleSave = async () => {
    if (!course) return;
    if (course.saved) await http.delete(`/support/university/courses/${courseId}/save`).catch(() => {});
    else await http.post(`/support/university/courses/${courseId}/save`).catch(() => {});
    setCourse({ ...course, saved: !course.saved });
  };

  if (loading) return <div className="p-6 lg:px-10 lg:pt-10"><div className="animate-pulse h-40 bg-white rounded-3xl" /></div>;
  if (!course) return <div className="p-10 text-navy/60">Course not found.</div>;

  // ── Lesson stepper ──
  if (active) {
    const cur = steps[step];
    const isLast = step === steps.length - 1;
    const pct = ((step + 1) / steps.length) * 100;
    return (
      <div className="min-h-screen bg-[#F9F8F6] p-6 lg:px-10 lg:pt-8 pb-32" data-testid="su-lesson-view">
        <div className="max-w-2xl mx-auto">
          <button onClick={closeLesson} data-testid="su-lesson-close" className="inline-flex items-center gap-1 text-sm text-navy/60 hover:text-navy">
            <ChevronLeft size={16} /> {course.title}
          </button>
          <div className="mt-4 flex items-center justify-between">
            <p className="text-xs font-semibold text-navy" data-testid="su-lesson-progress">Step {step + 1} of {steps.length}</p>
          </div>
          <div className="h-1.5 rounded-full bg-black/10 overflow-hidden mt-2">
            <div className="h-full bg-gold rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>

          {step === 0 && active.objective ? (
            <div className="bg-navy text-white rounded-3xl p-6 mt-6">
              <p className="text-[10px] uppercase tracking-widest text-gold font-semibold">In this lesson</p>
              <p className="mt-2 leading-relaxed">{active.objective}</p>
            </div>
          ) : null}

          <div className="bg-white rounded-3xl p-7 card-soft mt-5">
            <h1 className="font-heading text-2xl text-navy">{cur.heading}</h1>
            {cur.body ? <p className="text-navy/80 leading-relaxed mt-3">{cur.body}</p> : null}
            {cur.bullets?.map((b, i) => (
              <div key={i} className="flex gap-3 mt-3"><span className="w-1.5 h-1.5 rounded-full bg-gold mt-2.5 shrink-0" /><p className="text-navy/80 leading-relaxed">{b}</p></div>
            ))}
            {cur.scripts?.map((s, i) => (
              <div key={i} className="flex gap-2.5 bg-[#F3EFE7] rounded-2xl p-4 mt-3 items-start">
                <MessageSquareQuote size={16} className="text-gold mt-0.5 shrink-0" />
                <p className="text-navy italic leading-relaxed">{s}</p>
              </div>
            ))}
            {cur.prompts?.length ? (
              <>
                <p className="font-heading text-lg text-navy mt-6 mb-2">Reflection</p>
                {cur.prompts.map((p, i) => <div key={`${i}-${p.slice(0,24)}`} className="bg-[#F9F8F6] rounded-2xl p-4 mt-2 text-navy italic leading-relaxed">{p}</div>)}
              </>
            ) : null}
            {cur.actions?.length ? (
              <>
                <p className="font-heading text-lg text-navy mt-6 mb-2">Try this</p>
                {cur.actions.map((a, i) => <div key={`${i}-${a.slice(0,24)}`} className="flex gap-2.5 mt-2 items-start"><Check size={16} className="text-gold mt-1 shrink-0" /><p className="text-navy/80 leading-relaxed">{a}</p></div>)}
              </>
            ) : null}
            {cur.practice ? (
              <>
                <p className="text-navy/80 leading-relaxed">Reading is a start — but the words land differently once you&rsquo;ve said them out loud. Your Support Coach can privately role-play this exact situation with you, as many times as you need, before you have the real conversation.</p>
                <div className="bg-[#F3EFE7] rounded-2xl p-4 mt-4">
                  <p className="text-[10px] uppercase tracking-widest text-gold font-semibold">Start with</p>
                  <p className="text-navy italic leading-relaxed mt-1.5">{cur.practice}</p>
                </div>
                <button
                  onClick={() => nav(`/app/support-coach?prompt=${encodeURIComponent(cur.practice)}`)}
                  data-testid="su-lesson-practice-coach"
                  className="inline-flex items-center gap-2 rounded-full bg-gold text-white px-6 py-3 text-sm font-medium mt-4 hover:opacity-90 transition-smooth"
                >
                  <MessageSquareQuote size={16} /> Practice with your Coach
                </button>
              </>
            ) : null}
          </div>

          <div className="flex items-center justify-between mt-6">
            <button onClick={() => (step === 0 ? closeLesson() : setStep(step - 1))} data-testid="su-lesson-prev" className="inline-flex items-center gap-1.5 text-sm text-navy/60 hover:text-navy">
              <ArrowLeft size={16} /> {step === 0 ? 'Close' : 'Back'}
            </button>
            <button onClick={next} data-testid="su-lesson-next" className="inline-flex items-center gap-2 rounded-full bg-navy text-white px-7 py-3 text-sm font-medium hover:bg-navy-soft">
              {isLast ? 'Complete lesson' : 'Next'} {isLast ? <Check size={16} /> : <ArrowRight size={16} />}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Course overview ──
  const prog = course.progress || { pct: 0, status: 'not_started' };
  const done = prog.status === 'completed';
  return (
    <div className="p-6 lg:px-10 lg:pt-8 pb-24" data-testid="su-course-view">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between">
          <button onClick={() => nav('/app/support-university')} data-testid="su-course-back" className="inline-flex items-center gap-1 text-sm text-navy/60 hover:text-navy">
            <ChevronLeft size={16} /> Support University
          </button>
          <button onClick={toggleSave} data-testid="su-course-save" className="p-2 rounded-full hover:bg-black/5">
            <Bookmark size={20} className={course.saved ? 'text-gold fill-gold' : 'text-navy/50'} />
          </button>
        </div>

        <h1 className="font-heading text-3xl lg:text-4xl text-navy mt-4 leading-tight">{course.title}</h1>
        {course.subtitle ? <p className="text-navy/60 mt-2 leading-relaxed">{course.subtitle}</p> : null}
        <p className="text-xs text-navy/40 mt-3">{course.lesson_count} lessons · {course.est_minutes} min · {course.level}</p>

        {done ? (
          <div className="flex items-center gap-3 bg-[#F3EFE7] rounded-2xl p-4 mt-5">
            <Trophy size={20} className="text-gold" />
            <p className="text-sm text-navy font-medium">You&rsquo;ve completed this course. Well done.</p>
          </div>
        ) : (
          <div className="mt-5">
            <div className="h-1.5 rounded-full bg-black/10 overflow-hidden"><div className="h-full bg-gold rounded-full" style={{ width: `${prog.pct}%` }} /></div>
            <p className="text-xs text-navy/50 mt-1.5">{prog.pct}% complete</p>
          </div>
        )}

        {(course.objectives || []).length ? (
          <div className="mt-7">
            <p className="font-heading text-lg text-navy mb-2">What you&rsquo;ll learn</p>
            {course.objectives.map((o, i) => <div key={`${i}-${o.slice(0,24)}`} className="flex gap-3 mt-2"><span className="w-1.5 h-1.5 rounded-full bg-gold mt-2.5 shrink-0" /><p className="text-navy/80 leading-relaxed">{o}</p></div>)}
          </div>
        ) : null}

        {(() => {
          const lessons = course.lessons || [];
          const resume = resumeTarget(lessons);
          const started = resume && ((resume.last_step || 0) > 0 || prog.pct > 0);
          if (!resume) return null;
          return (
            <button
              onClick={() => openLesson(resume)}
              data-testid="su-course-resume"
              className="w-full text-left flex items-center gap-4 bg-navy text-white rounded-3xl p-5 mt-7 hover:bg-navy-soft transition-smooth"
            >
              <span className="w-11 h-11 rounded-2xl bg-gold/20 flex items-center justify-center shrink-0">
                <Play size={18} className="text-gold fill-gold" />
              </span>
              <span className="flex-1">
                <span className="block text-[10px] uppercase tracking-widest text-gold font-semibold">{started ? 'Pick up where you left off' : 'Start learning'}</span>
                <span className="block font-medium mt-0.5">{resume.title}</span>
              </span>
              <ArrowRight size={18} className="text-white/60" />
            </button>
          );
        })()}

        <p className="font-heading text-lg text-navy mt-8 mb-3">Lessons</p>
        <div className="space-y-6">
          {moduleize(course.lessons || []).map((mod, mi) => {
            const doneCount = mod.lessons.filter((l) => l.completed).length;
            return (
              <div key={mod.name || `mod-${mi}`} data-testid={`su-module-${mi}`}>
                {mod.name ? (
                  <div className="flex items-center justify-between mb-2.5">
                    <p className="text-[11px] uppercase tracking-widest text-navy/45 font-semibold">Module {mi + 1} · {mod.name}</p>
                    <p className="text-[11px] text-navy/40">{doneCount}/{mod.lessons.length} done</p>
                  </div>
                ) : null}
                <div className="space-y-3">
                  {mod.lessons.map((l, idx) => {
                    const inProgress = !l.completed && (l.last_step || 0) > 0;
                    return (
                      <button
                        key={l.id}
                        onClick={() => openLesson(l)}
                        data-testid={`su-lesson-${l.id}`}
                        className="w-full text-left flex items-center gap-4 bg-white rounded-2xl p-4 card-soft hover:-translate-y-0.5 transition-smooth"
                      >
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-sm font-bold ${l.completed ? 'bg-gold text-white' : 'bg-[#F3EFE7] text-navy'}`}>
                          {l.completed ? <Check size={16} /> : mod.start + idx + 1}
                        </span>
                        <span className="flex-1">
                          <span className="flex items-center gap-2">
                            <span className="font-medium text-navy">{l.title}</span>
                            {inProgress ? <span className="text-[10px] font-semibold text-gold bg-gold/10 rounded-full px-2 py-0.5" data-testid={`su-lesson-inprogress-${l.id}`}>In progress</span> : null}
                          </span>
                          {l.objective ? <span className="block text-sm text-navy/55 mt-0.5">{l.objective}</span> : null}
                        </span>
                        <ArrowRight size={16} className="text-navy/30" />
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {done ? (
          <button onClick={() => nav('/app/support-university')} data-testid="su-return" className="w-full mt-8 rounded-full bg-navy text-white py-3.5 text-sm font-medium hover:bg-navy-soft">
            Return to Support University
          </button>
        ) : null}
      </div>
    </div>
  );
}
